'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MdArrowBack, MdPrint } from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    getBankAccountStatement,
    BankStatement,
    StatementMovement,
} from '@/lib/api/financial/bank-account-api'

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDate = (date: string) =>
    new Date(date + 'T00:00:00').toLocaleDateString('pt-BR')

const getDefaultDates = () => {
    const now = new Date()
    return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
        endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10),
    }
}

export default function BankAccountStatementPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [data, setData] = useState<BankStatement | null>(null)
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(getDefaultDates().startDate)
    const [endDate, setEndDate] = useState(getDefaultDates().endDate)

    useEffect(() => {
        loadStatement()
    }, [startDate, endDate])

    const loadStatement = async () => {
        if (!id) return
        setLoading(true)
        try {
            const res = await getBankAccountStatement(Number(id), startDate, endDate)
            if (res.success && res.result) {
                setData(res.result)
            }
        } catch {
            toast.error('Erro ao carregar extrato bancário')
        } finally {
            setLoading(false)
        }
    }

    const totalDebits = data
        ? data.movements.reduce((sum, m) => sum + m.debit, 0)
        : 0

    const totalCredits = data
        ? data.movements.reduce((sum, m) => sum + m.credit, 0)
        : 0

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <h1 className="text-2xl font-bold">
                        Extrato Bancário{data ? ` - ${data.account.name}` : ''}
                    </h1>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>
                    <MdPrint size={18} />
                </button>
            </div>

            {/* Filtro de período */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <label className="text-sm font-medium">Período:</label>
                <input
                    type="date"
                    className="input input-bordered input-sm"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                />
                <span className="text-sm text-base-content/60">até</span>
                <input
                    type="date"
                    className="input input-bordered input-sm"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                />
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="card bg-base-100 shadow-sm border border-base-300/50">
                                <div className="card-body p-4">
                                    <div className="skeleton h-3 w-24 mb-2"></div>
                                    <div className="skeleton h-6 w-32"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="card bg-base-100 shadow print:shadow-none">
                        <div className="p-4 space-y-3">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="skeleton h-8 w-full"></div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Conteúdo */}
            {!loading && data && (
                <div className="flex flex-col gap-6">
                    {/* Resumo */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="card bg-base-100 shadow-sm print:shadow-none border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Saldo de Abertura</span>
                                <p className={`text-xl font-bold ${data.openingBalance >= 0 ? 'text-success' : 'text-error'}`}>
                                    {formatCurrency(data.openingBalance)}
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm print:shadow-none border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Total Débitos</span>
                                <p className="text-xl font-bold text-error">
                                    {formatCurrency(totalDebits)}
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm print:shadow-none border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Total Créditos</span>
                                <p className="text-xl font-bold text-success">
                                    {formatCurrency(totalCredits)}
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm print:shadow-none border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Saldo Final</span>
                                <p className={`text-xl font-bold ${data.closingBalance >= 0 ? 'text-success' : 'text-error'}`}>
                                    {formatCurrency(data.closingBalance)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tabela de movimentações */}
                    {data.movements.length === 0 ? (
                        <div className="card bg-base-100 shadow print:shadow-none border border-base-300/50">
                            <div className="card-body items-center py-16">
                                <p className="text-base-content/50 text-lg">
                                    Nenhuma movimentação encontrada no período selecionado.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="card bg-base-100 shadow print:shadow-none overflow-x-auto">
                            <table className="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Descrição</th>
                                        <th className="text-right">Débito</th>
                                        <th className="text-right">Crédito</th>
                                        <th className="text-right">Saldo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.movements.map((mov) => (
                                        <tr key={mov.id}>
                                            <td className="font-mono whitespace-nowrap">
                                                {formatDate(mov.date)}
                                            </td>
                                            <td>{mov.description}</td>
                                            <td className="text-right font-mono text-error">
                                                {mov.debit > 0 ? formatCurrency(mov.debit) : '-'}
                                            </td>
                                            <td className="text-right font-mono text-success">
                                                {mov.credit > 0 ? formatCurrency(mov.credit) : '-'}
                                            </td>
                                            <td className={`text-right font-mono font-semibold ${mov.balance >= 0 ? 'text-success' : 'text-error'}`}>
                                                {formatCurrency(mov.balance)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Estado vazio sem dados */}
            {!loading && !data && (
                <div className="card bg-base-100 shadow print:shadow-none border border-base-300/50">
                    <div className="card-body items-center py-16">
                        <p className="text-base-content/50 text-lg">
                            Não foi possível carregar o extrato.
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
